import { faCommentDots, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import {
  faArrowDownToLine,
  faLocationDot,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getBarcode, getPayableBarcode } from "api/IamApi/entities/ezpay";
import errorNoBarcodeImage from "assets/error_gear_graphic.svg";
import XButton from "assets/icons/x_button.svg";
import { AppleWalletButton } from "components/ezpay/common/apple-wallet-button";
import { GoogleWalletButton } from "components/ezpay/common/google-wallet-button";
import { customStyles } from "constants/customStyles";
import { CASH_MODAL, COMMON_NAMESPACE } from "constants/i18n-namespaces";
import { EMPTY, ERROR } from "constants/Status";
import { useLabelsWithKrn, useRetailer } from "hooks";
import html2canvas from "html2canvas";
import { IconButton, TextButton } from "kubra-ux-forge";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import { useSelector } from "react-redux";
import { getRetailers, getRetailersLoadStatus } from "selectors";
import { getIsMobile } from "selectors/appState";
import { getPayableSession } from "selectors/payableSession";
import { IBiller } from "types/biller";
import { IRetailer } from "types/retailer";
import { getLabelWithKrn, isIOSDevice } from "utilities/helper";
import { account } from "utilities/preload-state";
import { BarcodeView } from "../barcode-view";
import { EmailModal } from "../email-modal";
import { RetailerView } from "../retailer-view";
import { SMSModal } from "../sms-modal";

export interface ICashViewProps {
  biller: IBiller;
  accountNumber: string;
  billerLogo: string;
  flowCallback: (arg0: string) => any;
  realm: string;
  emailInputChange: (arg0: string) => any;
  smsInputChange: (arg0: string) => any;
  payableTypeName?: string;
  payableAmount?: number;
  payableKrn?: string;
  selectedAmountOptionLabel?: string;
}

export const CashView = (props: ICashViewProps) => {
  const {
    biller,
    accountNumber,
    billerLogo,
    flowCallback,
    realm,
    emailInputChange,
    smsInputChange,
    payableTypeName,
    payableAmount,
    payableKrn,
    selectedAmountOptionLabel,
  } = props;
  const { t, i18n } = useTranslation(CASH_MODAL);
  const [isRetailerView, setIsRetailerView] = useState(false);
  const [barcodeView, setBarcodeView] = useState(true);
  const [emailModal, setEmailModal] = useState(false);
  const [smsModal, setSmsModal] = useState(false);
  const loadRetailerStatus = useSelector(getRetailersLoadStatus);
  const { loadRetailers } = useRetailer();
  const [barcodeText, setBarcodeText] = useState("init");
  const [barcodeLoadStatus, setBarcodeLoadStatus] = useState("loading");
  const [retailerSlice, setRetailerSlice] = useState<IRetailer[]>();
  const [fullRetailers, setFullRetailers] = useState<IRetailer[]>();
  const retailers = useSelector(getRetailers);
  const [serviceFee, setServiceFee] = useState(0);
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const currentLanguage = i18n.language;
  const isMobile = useSelector(getIsMobile);

  const payableSession = useSelector(getPayableSession);
  const getpayableNameLabel = getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, payableSession?.payable?.typeKrn ?? null, payableTypeName ?? "")
  const payableTypeNameLabel = !getpayableNameLabel ? payableTypeName : getpayableNameLabel;
  const payableQuantityFromPayable =
    payableSession?.payable && "quantity" in payableSession.payable
      ? payableSession.payable.quantity
      : undefined;
  const payableQuantity =
    payableSession?.payableItemResponse?.quantity ?? payableQuantityFromPayable;

  const payableCategory = payableSession?.payable?.category;
  const supportedBusinessUnitCount =
    payableSession?.payableType?.supportedBusinessUnitCodes?.length ?? 0;
  const payableTypeKrn =
    payableSession?.payable?.typeKrn ?? payableSession?.payableType?.typeKrn ?? null;
  const resolvedBusinessUnitCode =
    payableSession?.payableType?.supportedBusinessUnitCodes?.find(
      (code) =>
        code.toLowerCase() ===
        (payableSession?.payable?.businessUnitCode ?? "").toLowerCase()
    ) ?? payableSession?.payable?.businessUnitCode ?? "";
  const resolvedBusinessUnitValue = getLabelWithKrn(
    labelSourceWithKrn?.labelsWithKrn,
    payableTypeKrn,
    resolvedBusinessUnitCode
  );

  // Amount is sent for invoice, order, and freeform payables (not account).
  const passAmount =
    payableCategory && payableCategory !== "account"
      ? payableAmount ?? payableSession?.payable?.payableAmount ?? undefined
      : undefined;

  // Location (business unit) is sent for order and freeform payables only when more than one is supported.
  const passLocation =
    (payableCategory === "order" || payableCategory === "freeform") &&
    supportedBusinessUnitCount > 1
      ? resolvedBusinessUnitValue
      : undefined;

  // detect apple device
  const isAppleDevice = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

  const setModal = (method: string) => {
    if (method === "EmailModal") {
      setBarcodeView(false);
      setEmailModal(true);
      setSmsModal(false);
    } else if (method === "SMSModal") {
      setBarcodeView(false);
      setEmailModal(false);
      setSmsModal(true);
    } else if (method == "EmailModalSuccessClose") {
      setBarcodeView(true);
      setEmailModal(false);
      setSmsModal(false);
      flowCallback("EmailModalSuccessClose");
    } else if (method == "SmsModalSuccessClose") {
      setBarcodeView(true);
      setEmailModal(false);
      setSmsModal(false);
      flowCallback("SmsModalSuccessClose");
    } else {
      setBarcodeView(true);
      setEmailModal(false);
      setSmsModal(false);
    }
  };

  const setRetailerFlow = (
    isRetailer: boolean | ((prevState: boolean) => boolean)
  ) => {
    setIsRetailerView(isRetailer);
    if (isRetailer) {
      flowCallback("RetailerModal");
    } else flowCallback("CashModal");
  };

  const barcodeFileRef = useRef<File | null>(null);
  const barcodeFilePromiseRef = useRef<Promise<File | null> | null>(null);

  const buildBarcodeFile = useCallback(async (): Promise<File | null> => {
    const barcodeEl = document.getElementById("cashmodalbarcode") as HTMLElement | null;
    if (!barcodeEl) return null;

    const canvas = await html2canvas(barcodeEl, {
      useCORS: true,
      onclone: (doc) => {
        const hiddenDiv = doc.getElementById("cashmodalbarcode");
        if (hiddenDiv) hiddenDiv.style.display = "block";
      },
    });

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((createdBlob) => resolve(createdBlob), "image/png");
    });

    if (!blob) return null;

    return new File([blob], `${biller.billerName} Payment Barcode.png`, { type: "image/png" });
  }, [biller.billerName]);

  const ensureBarcodeFileReady = useCallback(async (): Promise<File | null> => {
    if (barcodeFileRef.current) {
      return barcodeFileRef.current;
    }

    if (!barcodeFilePromiseRef.current) {
      barcodeFilePromiseRef.current = buildBarcodeFile()
        .then((file) => {
          barcodeFileRef.current = file;

          if (!file) {
            console.warn("[CashView][BarcodeDownload] Barcode file build returned null.");
          }

          return file;
        })
        .catch((error) => {
          barcodeFileRef.current = null;
          console.warn("[CashView][BarcodeDownload] Barcode file build failed.", error);
          return null;
        })
        .finally(() => {
          barcodeFilePromiseRef.current = null;
        });
    }

    return barcodeFilePromiseRef.current;
  }, [buildBarcodeFile]);

  const downloadBarcode = async () => {
    if (isIOSDevice()) {
      const file = barcodeFileRef.current;

      if (!file) {
        console.info("[CashView][BarcodeDownload][iOS] File not ready at click time; warming cache.");
        void ensureBarcodeFileReady();
        return;
      }

      if (typeof navigator.share !== "function") {
        console.warn("[CashView][BarcodeDownload][iOS] navigator.share is unavailable.");
        return;
      }

      const canShareFiles =
        typeof navigator.canShare !== "function"
        || navigator.canShare({ files: [file] });

      if (!canShareFiles) {
        console.warn("[CashView][BarcodeDownload][iOS] navigator.canShare rejected file payload.");
        return;
      }

      try {
        await navigator.share({ files: [file] });
      } catch (error) {
        const shareError = error as DOMException;
        if (shareError?.name === "AbortError") {
          return;
        }

        console.warn("[CashView][BarcodeDownload][iOS] Share failed.", {
          name: shareError?.name,
          message: shareError?.message,
          userAgent: navigator.userAgent,
          hasNavigatorCanShare: typeof navigator.canShare === "function",
        });
      }
      return;
    }

    const file = barcodeFileRef.current ?? await ensureBarcodeFileReady();
    if (!file) return;

    const blobUrl = window.URL.createObjectURL(file);
    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = biller.billerName + " Payment Barcode.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    if (barcodeLoadStatus !== "loaded") {
      barcodeFileRef.current = null;
      barcodeFilePromiseRef.current = null;
      return;
    }

    void ensureBarcodeFileReady();
    // barcode image content depends on these values
  }, [barcodeLoadStatus, barcodeText, serviceFee, payableAmount, payableCategory, payableTypeNameLabel, selectedAmountOptionLabel, ensureBarcodeFileReady]);

  useEffect(() => {
    if (loadRetailerStatus === EMPTY) {
      loadRetailers(biller.spaceKrn, biller.clientId, biller.realm);
    } else if (loadRetailerStatus === ERROR) {
      console.log("Error retrieving retailers for RCP");
      // Ensure barcode view shows error state when retailer load fails
      setBarcodeLoadStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRetailerStatus, biller.clientId]);

  useEffect(() => {
    if (retailers && biller.clientId) {
      if (payableTypeName !== EMPTY && payableKrn != undefined) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getPayableBarcode(payableKrn!, biller.realm)
          .then((res: any) => {
            setBarcodeText(res.barcode);
            setServiceFee(res.serviceFee);
            setBarcodeLoadStatus("loaded");
          })
          .catch((err) => {
            setBarcodeLoadStatus("error");
          });
      }
      else {
        getBarcode(biller.clientId, biller.realm)
          .then((res: any) => {
            setBarcodeText(res.barcode);
            setServiceFee(res.serviceFee);
            setBarcodeLoadStatus("loaded");
          })
          .catch((err) => {
            setBarcodeLoadStatus("error");
          });
      }

      if (retailerSlice === undefined || retailerSlice.length === 0) {
        setFullRetailers(retailers);
        if (retailers.length > 3) {
          setRetailerSlice(
            retailers
              ?.filter((retailer) => {
                return retailer.enabled === true && retailer.preferred === true;
              })
              .sort((a, b) => a.orderNo - b.orderNo)
              .slice(0, 3)
          );

          setFullRetailers(
            retailers
              ?.filter((retailer) => {
                return retailer.enabled === true;
              })
              .sort((a, b) => a.orderNo - b.orderNo)
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller.clientId, retailers]);

  return (
    <div>
      {!isRetailerView && barcodeLoadStatus == "error" && (
        <div className="cash-view">
          <div className="cash-only-primary-display">
            <div className="ez-pay-no-barcode-content-center">
              {/* <Banner
            messageLabel={`${t("barcode-generation-error")}`}
            variant="error"
            withIcon
          /> */}
              <img
                data-testid="error-no-barcode-found"
                src={errorNoBarcodeImage}
                alt="could-not-load-barcode"
              ></img>
              <div className="could-not-load-barcode">
                {t("barcode-generation-error")}
              </div>
            </div>
          </div>
        </div>
      )}
      {!isRetailerView && (
        <div className="cash-view">
          {barcodeLoadStatus !== "error" && (
            <div className="cash-only-primary-display">
              {barcodeView && (
                <div data-testid="barcode-view">
                  <BarcodeView
                    billerLogo={billerLogo}
                    billerName={biller.billerName}
                    accountNumber={accountNumber}
                    barcodeText={barcodeText}
                    serviceFee={serviceFee}
                    payableTypeNameLabel={payableTypeNameLabel}
                    payableAmount={payableAmount}
                    payableCategory={payableCategory}
                    selectedAmountOptionLabel={selectedAmountOptionLabel}
                  />
                </div>
              )}
            </div>
          )}
          <div></div>
          {barcodeLoadStatus !== "error" && (
            <div className="cash-method">
              <div className="content-row">
                <div className="save-barcode-container-title">
                  {t("save-barcode-label")}
                </div>
              </div>
              <div className="save-barcode-icons-container">
                <IconButton
                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                  label={`${t("get-barcode-email-label")}`}
                  onClick={() => {
                    setModal("EmailModal");
                  }}
                  className="icon-button"
                  title={`${t("get-barcode-email-button-title")}`}
                  dataCy="icon-button-email-view"
                />
                <IconButton
                  icon={<FontAwesomeIcon icon={faCommentDots} />}
                  label={`${t("get-barcode-sms-label")}`}
                  onClick={() => {
                    setModal("SMSModal");
                  }}
                  className="icon-button"
                  title={`${t("get-barcode-sms-button-title")}`}
                  dataCy="icon-button-sms-view"
                />
                <IconButton
                  icon={<FontAwesomeIcon icon={faArrowDownToLine} />}
                  label={`${t("download-barcode-label")}`}
                  onClick={() => downloadBarcode()}
                  className="icon-button"
                  title={`${t("download-barcode-button-title")}`}
                />
                <GoogleWalletButton
                  biller={biller}
                  itemNumber={accountNumber}
                  barcodeText={barcodeText}
                  realm={realm}
                  language={!currentLanguage ? "en-US" : currentLanguage}
                  payableItemTypeLabel={!payableTypeNameLabel ? "Account" : payableTypeNameLabel} //Defaulting payable name to Account when not found
                  serviceFee={serviceFee}
                  quantity={payableQuantity}
                  amount={passAmount}
                  location={passLocation}
                />

                {isAppleDevice ? (
                  <AppleWalletButton
                    href="#"
                    biller={biller}
                    itemNumber={accountNumber}
                    barcodeText={barcodeText}
                    realm={realm}
                    language={!currentLanguage ? "en-US" : currentLanguage}
                    payableItemTypeLabel={!payableTypeNameLabel ? "Account" : payableTypeNameLabel} //Defaulting payable name to Account when not found
                    serviceFee={serviceFee}
                    quantity={payableQuantity}
                    amount={passAmount}
                    location={passLocation}
                  />
                ) : null}
              </div>
            </div>
          )}
          <div className="cash-retailers">
            <div className="content-row">
              <div className="save-barcode-container-title">
                {Array.isArray(retailers) &&
                  retailers.some((item) => item.preferred) &&
                  t("pay-with-retailers-label")}
              </div>
              <div></div>
            </div>
            <div
              className="retailer-logos"
              title={`${t("retailer-logos-title")}`}
            >
            {retailerSlice &&
                retailerSlice.map((retailer) => (
                  <img
                    key={retailer.id}
                    src={retailer.logo}
                    alt={retailer.name}
                    className="retailer-logo-img"
                  />
                ))}
            </div>
            <div className="content-row">
              <TextButton
                icon={<FontAwesomeIcon icon={faLocationDot} />}
                onClick={() => setRetailerFlow(true)}
                className="text-button"
                data-testid="viewRetailerButton"
                dataCy="button-find-a-retailer"
              >
                {t("find-a-retailer-label")}
              </TextButton>
            </div>
          </div>
        </div>
      )}
      {!isRetailerView && isMobile && (
        <Sheet
          className="cash-contact-sheet"
          isOpen={emailModal}
          onClose={() => setModal("BarcodeView")}
          detent="content-height"
          disableDrag={true}
        >
          <Sheet.Container>
            <Sheet.Content>
              <EmailModal
                cancelHandler={setModal}
                emailInputChange={emailInputChange}
              />
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      )}
      {!isRetailerView && !isMobile && emailModal && (
        <Modal
          isOpen={emailModal}
          ariaHideApp={true}
          className="modal-frame-help"
          shouldCloseOnOverlayClick={false}
          style={customStyles}
        >
          <EmailModal
            cancelHandler={setModal}
            emailInputChange={emailInputChange}
          />
        </Modal>
      )}
      {!isRetailerView && isMobile && (
        <Sheet
          className="cash-contact-sheet"
          isOpen={smsModal}
          onClose={() => setModal("BarcodeView")}
          detent="content-height"
          disableDrag={true}
        >
          <Sheet.Container>
            <Sheet.Content>
              <SMSModal
                billerName={biller.billerName}
                cancelHandler={setModal}
                accountNumber={accountNumber}
                barcodeText={barcodeText}
                smsInputChange={smsInputChange}
              />
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      )}
      {!isRetailerView && !isMobile && smsModal && (
        <Modal
          isOpen={smsModal}
          ariaHideApp={true}
          className="modal-frame-help"
          shouldCloseOnOverlayClick={false}
          style={customStyles}
        >
          <SMSModal
            billerName={biller.billerName}
            cancelHandler={setModal}
            accountNumber={accountNumber}
            barcodeText={barcodeText}
            smsInputChange={smsInputChange}
          />
        </Modal>
      )}
      {isMobile && (
        <Sheet
          isOpen={isRetailerView}
          onClose={() => setIsRetailerView(false)}
          detent="content-height"
          disableDrag={true}
        >
          <Sheet.Container>
            <Sheet.Header>
              <div className="modal-retailer-header">
                <button
                  className="modal-close-mobile"
                  onClick={() => setIsRetailerView(false)}
                  aria-label={`${t("close-modal", {
                    ns: COMMON_NAMESPACE,
                  })}`}
                >
                  <img
                    src={XButton}
                    alt=""
                  />
                </button>
              </div>
            </Sheet.Header>
            <Sheet.Content>
              <RetailerView
                defaultRetailerId="0"
                defaultZipCode={
                  account.validation?.mailingAddress?.postalCode ?? ""
                }
                mapMode={false}
                retailers={fullRetailers}
                isOpen={true}
              />
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      )}
      {!isMobile && isRetailerView && (
        <div>
          <div className="retail-view">
            <Modal
              isOpen={isRetailerView}
              ariaHideApp={true}
              className="modal-frame-help"
              shouldCloseOnOverlayClick={false}
              style={customStyles}
            >
              <div className="modal-retailer-header">
                <button
                  className="modal-close-retailer"
                  onClick={() => setIsRetailerView(false)}
                  aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                >
                  <img
                    src={XButton}
                    alt=""
                    className="modal-close-x"
                  />
                </button>
              </div>
              <RetailerView
                defaultRetailerId="0"
                defaultZipCode={
                  account.validation?.mailingAddress?.postalCode ?? ""
                }
                mapMode={false}
                retailers={fullRetailers}
                isOpen={true}
              />
            </Modal>
          </div>
        </div>
      )}
      <div
        id="cashmodalbarcode"
        className="barcode-hidden"
        aria-hidden="true"
        title="hidden barcode"
      >
        <div className="cash-only-primary-display">
          <BarcodeView
            billerName={biller.billerName}
            billerLogo={billerLogo}
            accountNumber={accountNumber}
            barcodeText={barcodeText}
            serviceFee={serviceFee}
            payableTypeNameLabel={payableTypeNameLabel}
            payableAmount={payableAmount}
            payableCategory={payableCategory}
            selectedAmountOptionLabel={selectedAmountOptionLabel}
          />
        </div>
      </div>
    </div>
  );
};
export default CashView;
